/**
 * ProductionForecastDashboard Component
 * US-013D: Main dashboard for 6-month production forecasting
 *
 * Features:
 * - Plan overview with strain breakdown
 * - Upcoming milestones
 * - Gap analysis alerts
 * - Daily tasks integration
 * - Plan generation controls
 */

import { useState, useCallback } from 'react'
import { toLocalDateString } from '@/utils/dateUtils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PageLayout } from '@/components/layout'
import useProductionForecast from './useProductionForecast'
import useCapacityUtilisation from './useCapacityUtilisation'
import CapacityUtilisationChart from './CapacityUtilisationChart'
import EquipmentDowntime from './EquipmentDowntime'
import BumpTaskModal from './BumpTaskModal'
import { completeTask } from './productionForecastService'
import { toast } from 'sonner'
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle2,
  ClipboardList,
  Clock,
  Loader2,
  RefreshCw,
  Sparkles,
  TrendingUp
} from 'lucide-react'
import { cn } from '@/lib/utils'

// =============================================================================
// Sub-components
// =============================================================================

function PlanOverviewCard({ plan, loading }) {
  const totalBlocks = plan.reduce((sum, entry) => sum + (entry.planned_blocks || 0), 0)
  const totalDemand = plan.reduce((sum, entry) => sum + (parseFloat(entry.demand_kg) || 0), 0)
  const weeksWithPlan = new Set(plan.map(e => e.target_week)).size

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          Plan Overview
        </CardTitle>
        <CardDescription>6-month forward production plan</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{weeksWithPlan}</p>
              <p className="text-xs text-muted-foreground">Weeks Planned</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{totalDemand.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">Total kg</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{totalBlocks}</p>
              <p className="text-xs text-muted-foreground">Blocks</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function StrainBreakdownCard({ planByStrain, loading }) {
  const strainData = Array.from(planByStrain.entries()).map(([name, entries]) => ({
    name,
    totalKg: entries.reduce((sum, e) => sum + (parseFloat(e.demand_kg) || 0), 0),
    totalBlocks: entries.reduce((sum, e) => sum + (e.planned_blocks || 0), 0),
    weeksPlanned: new Set(entries.map(e => e.target_week)).size
  })).sort((a, b) => b.totalKg - a.totalKg)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Strain Breakdown</CardTitle>
        <CardDescription>Production by strain</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : strainData.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No strains in plan
          </p>
        ) : (
          <div className="space-y-3">
            {strainData.slice(0, 5).map((strain) => (
              <div key={strain.name} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{strain.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {strain.weeksPlanned} weeks, {strain.totalBlocks} blocks
                  </p>
                </div>
                <Badge variant="secondary">{strain.totalKg.toFixed(0)} kg</Badge>
              </div>
            ))}
            {strainData.length > 5 && (
              <p className="text-xs text-muted-foreground text-center">
                +{strainData.length - 5} more strains
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function UpcomingMilestonesCard({ milestones, loading }) {
  // Transform milestones into displayable format
  const upcomingActions = []

  for (const entry of milestones.slice(0, 10)) {
    const strainName = entry.strain?.name || 'Unknown'

    if (entry.planned_lc_start) {
      upcomingActions.push({
        date: entry.planned_lc_start,
        type: 'LC Start',
        strain: strainName,
        blocks: entry.planned_blocks
      })
    }
    if (entry.planned_spawn_start) {
      upcomingActions.push({
        date: entry.planned_spawn_start,
        type: 'Spawn Start',
        strain: strainName,
        blocks: entry.planned_blocks
      })
    }
    if (entry.planned_substrate_start) {
      upcomingActions.push({
        date: entry.planned_substrate_start,
        type: 'Substrate Prep',
        strain: strainName,
        blocks: entry.planned_blocks
      })
    }
    if (entry.planned_fruiting_start) {
      upcomingActions.push({
        date: entry.planned_fruiting_start,
        type: 'Move to Fruiting',
        strain: strainName,
        blocks: entry.planned_blocks
      })
    }
  }

  // Sort by date and take first 8
  upcomingActions.sort((a, b) => a.date.localeCompare(b.date))
  const displayActions = upcomingActions.slice(0, 8)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          Upcoming Milestones
        </CardTitle>
        <CardDescription>Next 14 days</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : displayActions.length === 0 ? (
          <div className="text-center py-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No upcoming milestones</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayActions.map((action, idx) => (
              <div
                key={`${action.date}-${action.type}-${idx}`}
                className="flex items-center justify-between py-1 border-b border-border last:border-0"
              >
                <div>
                  <p className="text-sm font-medium">{action.type}</p>
                  <p className="text-xs text-muted-foreground">
                    {action.strain} ({action.blocks} blocks)
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {formatDateShort(action.date)}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function DailyTasksCard({
  tasks,
  tasksSummary,
  loading,
  error,
  onBumpTask,
  onCompleteTask,
  completingTaskId
}) {
  const today = toLocalDateString(new Date())
  const todaysTasks = tasks.filter(t => t.planned_date === today)
  const overdueTasks = tasks.filter(t => t.is_overdue)

  return (
    <Card className="col-span-full lg:col-span-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          Today's Tasks
        </CardTitle>
        <CardDescription>From production plan</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-3">
            {overdueTasks.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Overdue</AlertTitle>
                <AlertDescription className="text-amber-700">
                  {overdueTasks.length} task{overdueTasks.length !== 1 ? 's' : ''} need attention
                </AlertDescription>
              </Alert>
            )}

            {todaysTasks.length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No production tasks for today</p>
              </div>
            ) : (
              <div className="space-y-2">
                {todaysTasks.slice(0, 5).map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onBump={() => onBumpTask?.(task)}
                    onComplete={() => onCompleteTask?.(task)}
                    isCompleting={completingTaskId === task.id}
                  />
                ))}
                {todaysTasks.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{todaysTasks.length - 5} more tasks
                  </p>
                )}
              </div>
            )}

            {tasksSummary && (
              <div className="pt-3 border-t border-border grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-red-600">
                    {tasksSummary.by_priority?.[1] || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Critical</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-orange-600">
                    {tasksSummary.by_priority?.[2] || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">High</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-blue-600">
                    {tasksSummary.by_priority?.[3] || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Medium</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-600">
                    {tasksSummary.by_priority?.[4] || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Low</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TaskRow({ task, onBump, onComplete, isCompleting }) {
  return (
    <div className="flex items-start gap-2 py-2 border-b border-border last:border-0 group">
      <PriorityDot priority={task.priority} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{task.description}</p>
        {task.strain_name && (
          <p className="text-xs text-muted-foreground">{task.strain_name}</p>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onComplete}
          disabled={isCompleting}
          title="Mark complete"
        >
          {isCompleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5 text-emerald-600" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onBump}
          title="Reschedule task"
        >
          <Clock className="h-3.5 w-3.5 text-amber-600" />
        </Button>
      </div>
      <Badge variant="outline" className="text-xs capitalize flex-shrink-0">
        {task.assigned_role?.replace('_', ' ')}
      </Badge>
    </div>
  )
}

function PriorityDot({ priority }) {
  const colors = {
    1: 'bg-red-500',
    2: 'bg-orange-500',
    3: 'bg-blue-500',
    4: 'bg-gray-400'
  }

  return (
    <div className={cn('w-2 h-2 rounded-full mt-1.5 flex-shrink-0', colors[priority] || colors[4])} />
  )
}

function PlanTable({ plan, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (plan.length === 0) {
    return (
      <div className="text-center py-8">
        <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No plan entries found</p>
        <p className="text-sm text-muted-foreground mt-1">
          Generate a plan to see entries here
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Target Week</th>
            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Strain</th>
            <th className="text-right py-2 px-3 font-medium text-muted-foreground">Demand (kg)</th>
            <th className="text-right py-2 px-3 font-medium text-muted-foreground">Blocks</th>
            <th className="text-left py-2 px-3 font-medium text-muted-foreground">LC Start</th>
            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Fruiting</th>
            <th className="text-center py-2 px-3 font-medium text-muted-foreground">Status</th>
          </tr>
        </thead>
        <tbody>
          {plan.map((entry) => (
            <tr key={entry.id} className="border-b border-border hover:bg-muted/50">
              <td className="py-2 px-3">{formatWeek(entry.target_week)}</td>
              <td className="py-2 px-3">{entry.strain?.name || 'Unknown'}</td>
              <td className="py-2 px-3 text-right">{parseFloat(entry.demand_kg).toFixed(1)}</td>
              <td className="py-2 px-3 text-right">{entry.planned_blocks}</td>
              <td className="py-2 px-3">{formatDateShort(entry.planned_lc_start)}</td>
              <td className="py-2 px-3">{formatDateShort(entry.planned_fruiting_start)}</td>
              <td className="py-2 px-3 text-center">
                <StatusBadge status={entry.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StatusBadge({ status }) {
  const variants = {
    planned: 'bg-blue-100 text-blue-700',
    in_production: 'bg-emerald-100 text-emerald-700',
    harvested: 'bg-gray-100 text-gray-700',
    missed: 'bg-red-100 text-red-700',
    cancelled: 'bg-orange-100 text-orange-700'
  }

  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize',
      variants[status] || variants.planned
    )}>
      {status?.replace('_', ' ')}
    </span>
  )
}

// =============================================================================
// Utility Functions
// =============================================================================

function formatDateShort(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })
}

function formatWeek(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return `W${getWeekNumber(date)} (${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`
}

function getWeekNumber(date) {
  const yearStart = new Date(date.getFullYear(), 0, 1)
  const days = Math.floor((date - yearStart) / (24 * 60 * 60 * 1000))
  return Math.ceil((days + yearStart.getDay() + 1) / 7)
}

// =============================================================================
// Main Component
// =============================================================================

export default function ProductionForecastDashboard({ user, onLogout }) {
  const {
    plan,
    planByStrain,
    upcomingMilestones,
    tasks,
    tasksSummary,
    loading,
    tasksLoading,
    generating,
    error,
    tasksError,
    strainFilter,
    setStrainFilter,
    generatePlan,
    refresh,
    loadTasks
  } = useProductionForecast({ autoLoad: true, weeksAhead: 26 })

  // Phase 2: Capacity utilisation hook
  const {
    chartData,
    byDate,
    conflicts,
    resources,
    downtimes,
    loading: capacityLoading,
    downtimesLoading,
    error: capacityError,
    hasConflicts,
    refresh: refreshCapacity,
    createDowntime,
    assessImpact
  } = useCapacityUtilisation({ autoLoad: true, daysAhead: 14 })

  const [activeTab, setActiveTab] = useState('overview')

  // Phase 3: Bump modal state
  const [bumpModalOpen, setBumpModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [completingTaskId, setCompletingTaskId] = useState(null)

  // Get unique strains for filter
  const strainOptions = Array.from(planByStrain.keys()).sort()

  const handleGeneratePlan = async () => {
    try {
      await generatePlan({ force_regenerate: true })
    } catch {
      // Error already handled in hook
    }
  }

  // Phase 3: Task bump handler
  const handleBumpTask = useCallback((task) => {
    setSelectedTask(task)
    setBumpModalOpen(true)
  }, [])

  // Phase 3: Task complete handler
  const handleCompleteTask = useCallback(async (task) => {
    setCompletingTaskId(task.id)
    try {
      const result = await completeTask({ taskId: task.id })
      toast.success(result.message || `${task.description} marked complete`)
      // Refresh tasks to update the list
      await loadTasks()
    } catch (err) {
      toast.error(err.message || 'Failed to complete task')
    } finally {
      setCompletingTaskId(null)
    }
  }, [loadTasks])

  // Phase 3: Bump success handler
  const handleBumpSuccess = useCallback(async (result) => {
    toast.success(result.message || 'Task has been bumped to the new date')
    // Refresh tasks to update the list
    await loadTasks()
  }, [loadTasks])

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="h-full overflow-y-auto">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Production Forecast</h1>
            <p className="text-muted-foreground">6-month forward planning from demand forecast</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
            <Button onClick={handleGeneratePlan} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Plan
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="plan">Full Plan</TabsTrigger>
            <TabsTrigger value="capacity" className="relative">
              <Activity className="h-4 w-4 mr-1" />
              Capacity
              {hasConflicts && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <PlanOverviewCard
                plan={plan}
                loading={loading}
              />
              <StrainBreakdownCard
                planByStrain={planByStrain}
                loading={loading}
              />
              <UpcomingMilestonesCard
                milestones={upcomingMilestones}
                loading={loading}
              />
              <DailyTasksCard
                tasks={tasks}
                tasksSummary={tasksSummary}
                loading={tasksLoading}
                error={tasksError}
                onBumpTask={handleBumpTask}
                onCompleteTask={handleCompleteTask}
                completingTaskId={completingTaskId}
              />
            </div>
          </TabsContent>

          {/* Full Plan Tab */}
          <TabsContent value="plan" className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Production Plan</CardTitle>
                    <CardDescription>{plan.length} entries</CardDescription>
                  </div>
                  <Select
                    value={strainFilter || 'all'}
                    onValueChange={(v) => setStrainFilter(v === 'all' ? null : v)}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by strain" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All strains</SelectItem>
                      {strainOptions.map((strain) => (
                        <SelectItem key={strain} value={strain}>{strain}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <PlanTable plan={plan} loading={loading} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Capacity Tab - Phase 2 */}
          <TabsContent value="capacity" className="space-y-6 mt-6">
            {/* Capacity Utilisation Chart */}
            <CapacityUtilisationChart
              chartData={chartData}
              byDate={byDate}
              resources={resources}
              conflicts={conflicts}
              loading={capacityLoading}
              error={capacityError}
              onRefresh={refreshCapacity}
            />

            {/* Equipment Downtime */}
            <EquipmentDowntime
              resources={resources}
              downtimes={downtimes}
              loading={downtimesLoading}
              onCreateDowntime={createDowntime}
              onAssessImpact={assessImpact}
            />
          </TabsContent>
        </Tabs>
      </div>
      </div>

        {/* Phase 3: Bump Task Modal */}
        <BumpTaskModal
          open={bumpModalOpen}
          onOpenChange={setBumpModalOpen}
          task={selectedTask}
          onSuccess={handleBumpSuccess}
        />
    </PageLayout>
  )
}
